// src/utils/resetLink.js
/**
 * Reading a password-reset token out of whatever URL the email actually used.
 *
 * The backend builds the reset link, not this app, and it has been observed
 * pointing at the site root rather than the portal's base path. Mail clients
 * and gateways also rewrite links (tracking redirects, fragment mangling).
 * We cannot control the shape of the incoming URL, so the router and the reset
 * page both parse it through here rather than each guessing separately.
 *
 * Two different sets of keys, deliberately:
 *   - TOKEN_KEYS is broad. Once the reset page is showing, the user clearly
 *     came from a reset link, so accepting a loosely-named param costs nothing.
 *   - REDIRECT_KEYS is narrow. Those are used to decide whether to yank a page
 *     away from what it was going to render, which needs a stronger signal —
 *     'code', 't' and 'id' are far too generic to justify that.
 */

const TOKEN_KEYS    = ['token', 'resetToken', 'reset_token', 'key', 'code', 'otp', 't', 'id'];
const REDIRECT_KEYS = ['token', 'resetToken', 'reset_token', 'key'];
const EMAIL_KEYS    = ['email', 'userEmailId', 'userName', 'username', 'u'];

/* Params can arrive in the query OR the fragment — some mailers and SSO
   bridges emit `#token=…`, which never reaches the server and so is invisible
   to `location.search`. */
function paramSets(search, hash) {
  const sets = [new URLSearchParams(search || '')];
  const frag = String(hash || '').replace(/^#/, '');
  if (frag.includes('=')) sets.push(new URLSearchParams(frag));
  return sets;
}

function pick(keys, search, hash) {
  for (const params of paramSets(search, hash)) {
    for (const key of keys) {
      const value = params.get(key);
      if (value && value.trim()) return value.trim();
    }
  }
  return '';
}

export const readResetToken = (search, hash) => pick(TOKEN_KEYS, search, hash);
export const readResetEmail = (search, hash) => pick(EMAIL_KEYS, search, hash);

/** Strong enough to redirect an unrelated route to the reset page. */
export const looksLikeResetLink = (search, hash) => Boolean(pick(REDIRECT_KEYS, search, hash));
